import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import { Button } from 'react-bootstrap';
import { Cell } from 'react-table';
import { decode } from 'html-entities';

import { useAppSelector } from 'hooks';
import { useAxios } from 'configs/requests';
import { SeverityIcon, Table } from 'components/UI';
import { Loading, Error } from 'components/Fetch';
import { isPatientArchived } from 'store/patient/reducer';

interface AlertsListProps {
  alerts: IPatientAlerts[];
  truncate?: boolean;
  allowReopen?: boolean;
  onReopen?: () => any;
}

const AlertsList = ({
  alerts,
  truncate,
  allowReopen = false,
  onReopen = undefined
}: AlertsListProps) => {
  const intl = useIntl();
  truncate = truncate && alerts.length > 2;
  const [expand, setExpand] = useState<boolean>(false);
  const [alertId, setAlertId] = useState('');
  const _isPatientArchived = useAppSelector(isPatientArchived);

  const [{ loading, error }, reopen] = useAxios(
    {
      method: 'PUT'
    },
    { useCache: false, manual: true }
  );

  const STATUS_OPEN = 0;

  const columns: any = [
    {
      id: 'date',
      align: 'right',
      Header: () => intl.formatMessage({ id: 'dateKey' }),
      Cell: ({ row: { original } }: Cell<IPatientAlerts>) => (
        <div className="d-inline-block">
          <p className="m-0">{original.date}</p>
          <small>{original.time}</small>
        </div>
      ),
      maxWidth: 100
    },
    {
      id: 'severity',
      align: 'center',
      Header: () => intl.formatMessage({ id: 'severityKey' }),
      Cell: ({ row: { original } }: Cell<IPatientAlerts>) => (
        <SeverityIcon name={original.severityImg} />
      ),
      maxWidth: 75
    },
    {
      id: 'title',
      Header: intl.formatMessage({ id: 'alertTitleKey' }),
      Cell: ({ row: { original } }: Cell<IPatientAlerts>) => decode(original.title)
    },
    {
      id: 'description',
      Header: intl.formatMessage({ id: 'descriptionKey' }),
      Cell: ({ row: { original } }: Cell<IPatientAlerts>) => decode(original.description),
      minWidth: 275
    }
  ];

  if (allowReopen) {
    columns.push({
      id: 'reopen',
      Cell: ({ row: { original } }: Cell<IPatientAlerts>) => {
        return (
          <Button
            variant="link"
            className="showOnHover"
            data-test={`alert-reopen-${original.alert_id}-btn`}
            onClick={() => handleReopen(original.alert_id)}
            disabled={_isPatientArchived}
          >
            {intl.formatMessage({ id: 'reopenKey' })}
          </Button>
        );
      },
      maxWidth: 100
    });
  }

  const handleReopen = (alertId: string) => {
    setAlertId(alertId);
    reopen({ url: `/clinician/Alert/${alertId}`, data: { status: STATUS_OPEN } }).then(
      (response) => {
        if (response.status === 200) {
          setAlertId('');
          onReopen && onReopen();
        }
      }
    );
  };

  return (
    <>
      <div
        className={`table table-layout-fixed bg-white ${
          truncate ? ' overflow-hidden' : ' overflow-auto'
        }  mb-1`}
        style={{ maxHeight: truncate ? (expand ? 'none' : '15vh') : '30vh' }}
        data-test="closed-alerts-table"
      >
        {truncate && !expand && <div className="fade-overlay-white" />}
        {loading && <Loading />}
        {error && alertId && <Error retry={() => handleReopen(alertId)} />}
        {!loading && <Table dataTestIdPrefix="closed" data={alerts} columns={columns} hideMargin />}
      </div>
      {truncate && (
        <div className="d-flex justify-content-center">
          <Button
            onClick={() => setExpand(!expand)}
            variant="link"
            data-test={`alerts-list-${expand ? 'collapse' : 'expand'}-btn`}
          >
            {intl.formatMessage({ id: expand ? 'hideKey' : 'showAllClosedAlertsKey' })}
          </Button>
        </div>
      )}
    </>
  );
};

export default AlertsList;
